import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "./useAdminServices";
import { useUnifiedNotificationStore } from "../stores/unifiedNotificationStore";
import { NotificationSummaryDto } from "../services/admin/types/notificationSummary.types";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useHotel } from "../contexts/HotelContext";

const STALE_TIME = 30 * 1000; // 30 seconds
const REFETCH_INTERVAL = 60 * 1000; // 60 seconds

export const useNotificationSummary = () => {
  const notificationService = useNotificationService();
  const queryClient = useQueryClient();
  const { setSummary, setLoading, setError, reset } = useUnifiedNotificationStore();
  const { myHotelInfo, isSwitching } = useHotel();
  const hotelId = myHotelInfo?.hotelId;
  const prevHotelIdRef = useRef<string | undefined>(undefined);
  const prevIsSwitchingRef = useRef<boolean>(false);

  // CRITICAL: Reset when switching STARTS - this ensures counters show 0 immediately
  // Also force refetch when switching ENDS to get fresh data for new hotel
  useEffect(() => {
    if (isSwitching && !prevIsSwitchingRef.current) {
      reset();
      queryClient.cancelQueries({ queryKey: ['notificationSummary'] });
      queryClient.removeQueries({ queryKey: ['notificationSummary'] });
    } else if (!isSwitching && prevIsSwitchingRef.current) {
      queryClient.invalidateQueries({ queryKey: ['notificationSummary'] });
    }
    prevIsSwitchingRef.current = isSwitching;
  }, [isSwitching, reset, queryClient]);

  // CRITICAL: Reset when hotelId changes to prevent any stale data
  useEffect(() => {
    if (hotelId && prevHotelIdRef.current && hotelId !== prevHotelIdRef.current) {
      reset();
      queryClient.removeQueries({ queryKey: ['notificationSummary'] });
    }
    prevHotelIdRef.current = hotelId;
  }, [hotelId, reset, queryClient]);

  const query = useQuery({
    // Include hotelId in query key for proper cache isolation per hotel
    queryKey: ["notificationSummary", hotelId],
    queryFn: async (): Promise<NotificationSummaryDto | null> => {
      if (!notificationService) {
        throw new Error("NotificationService not initialized");
      }

      const result = await notificationService.getSummary();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(
          result.error?.message || "Failed to fetch notification summary",
        );
      }
    },
    // Disable during hotel switching to prevent fetching stale data from old hotel
    enabled: !!notificationService && !!hotelId && !isSwitching,
    staleTime: STALE_TIME,
    gcTime: 0, // Don't cache between hotel switches - always fetch fresh
    refetchInterval: REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // Always refetch when component mounts (after hotel switch remount)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Update store when data changes
  // NOTE: hotelId intentionally NOT in deps - we only want to update when query.data changes
  // Having hotelId in deps caused stale data from old hotel to be set when switching hotels
  useEffect(() => {
    if (query.data) {
      setSummary(query.data);
    }
  }, [query.data, setSummary]);

  // Update loading state
  useEffect(() => {
    setLoading(query.isLoading);
  }, [query.isLoading, setLoading]);

  // Update error state
  useEffect(() => {
    if (query.error) {
      const errorMessage =
        query.error instanceof Error
          ? query.error.message
          : "Failed to fetch notifications";
      setError(errorMessage);

      // Only show toast for non-auth errors
      if (!errorMessage.includes("NotificationService not initialized")) {
        toast.error(errorMessage);
      }
    } else {
      setError(null);
    }
  }, [query.error, setError]);

  return {
    ...query,
    refetch: query.refetch,
  };
};
